<template>
  <div class="main-page-container newCompany" v-if="company">
    <b-modal id="editEntity" hide-footer hide-header size="md" scrollable>
      <EditEntity :cropImageData="cropImageData" />
    </b-modal>

    <cropperImage
      :area="cropArea"
      :type="cropType"
      :errorMessage="cropErrorMessage"
      :minHeight="45"
      :minWidth="200"
      :ratio="0"
      :maxSize="2097152"
      :message="cropMessage"
      @croppedImageEvent="showEditEntityModal"
      @closeCroppedImageModal="showEditEntityModal"
    />

    <VpFormLayout>
      <template v-slot:content-header>
        <PageHeader
          :title="title"
          className="d-flex align-items-center justify-content-between"
        />
      </template>
      <template v-slot:content-body>
        <AlertFoundActiveSession v-if="company.package == 'Cloud'" />

        <div class="card">
          <div class="card-body">
            <div class="text-center">
              <div class="camera-BG" v-if="!company.logo">
                <svg
                  width="31"
                  height="25"
                  style="opacity: 0.65"
                  viewBox="0 0 31 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    id="Vector"
                    opacity="0.65"
                    d="M10.4754 0L8.79936 3.37401H2.5141C1.12129 3.37401 0 4.50259 0 5.90451V21.931C0 23.3329 1.12129 24.4615 2.5141 24.4615H27.6551C29.0479 24.4615 30.1692 23.3329 30.1692 21.931V5.90451C30.1692 4.50259 29.0479 3.37401 27.6551 3.37401H21.3699L19.6938 0H10.4754ZM15.0846 7.59151C18.5559 7.59151 21.3699 10.4239 21.3699 13.9178C21.3699 17.4116 18.5559 20.244 15.0846 20.244C11.6134 20.244 8.79936 17.4116 8.79936 13.9178C8.79936 10.4239 11.6134 7.59151 15.0846 7.59151Z"
                    fill="#8C8C8C"
                  />
                </svg>
              </div>
              <p class="text-muted" v-if="!company.logo">
                <strong>{{ $t("common.notes.note") }}: </strong>
                {{ $t("companyDetails.notes.logo") }}
              </p>
              <div v-if="company.logo">
                <img :src="company.logo" alt="" class="img-company-bg" />
              </div>
              <b-button
                variant="primary"
                pill
                class="edit-btn"
                :disabled="disabledEdit"
                size="sm"
                v-b-modal="'editEntity'"
                @click.stop="setData(company)"
              >
                {{ $t("common.edit") }}
              </b-button>
              <div class="seperator-line"></div>
            </div>

            <div class="">
              <div class="company-info row">
                <div class="company-info__card col-12 col-sm-12 col-lg-4">
                  <div>
                    <h6 class="text-muted">
                      {{ $t("companyDetails.form.name") }}
                    </h6>
                    <p class="paragraph-muted">{{ company.name }}</p>
                  </div>
                  <div>
                    <h6 class="text-muted">{{ $t("common.phoneNumber") }}</h6>
                    <p class="paragraph-muted"><template v-if="getLanguage != 'ar'">+</template>{{ company.company_phone }}<template v-if="getLanguage== 'ar'">+</template></p>
                    <!-- <p> +{{ company.countryCode}}{{ company.company_phone }}</p> -->
                  </div>
                </div>

                <div class="company-info__card col-12 col-sm-12 col-lg-4">
                  <div>
                    <h6 class="text-muted">
                      {{ $t("companyDetails.form.subdomain") }}
                    </h6>
                    <p class="paragraph-muted">{{ company.subdomain }}</p>
                  </div>
                  <div>
                    <h6 class="text-muted">{{ $t("common.package") }}</h6>
                    <p class="paragraph-muted">{{ companyPackage }}</p>
                  </div>
                </div>

                <div class="company-info__card col-12 col-sm-12 col-lg-4">
                  <div>
                    <h6 class="text-muted">
                      {{ $t("common.numberOfKiosks") }}
                    </h6>
                    <p class="paragraph-muted">{{ company.kiosks }}</p>
                  </div>
                  <div>
                    <h6 class="text-muted">
                      {{ $t("common.temperatureUnit") }}
                    </h6>
                    <p v-if="temperature_unit == 'c'" class="paragraph-muted">
                      °{{$t("common.temperatureUnits.c")}}
                    </p>
                    <p v-else class="paragraph-muted">°{{$t("common.temperatureUnits.f")}}</p>
                  </div>
                </div>
              </div>

              <div class="seperator-line vp-my-20"></div>
            </div>

            <div class="vp-mt-20" :class="{ 'vp-mb-30': !token }">
              <h6 class="text-muted">
                {{ $t("companyDetails.form.apiIntegration") }}
              </h6>

              <!-- Generate Token -->
              <Transition mode="in-out">
                <div v-if="!token" class="vp-mb-10" style="position: absolute">
                  <button
                    class="btn btn-primary vp-mr-7 vp-mb-10"
                    :disabled="tokenLoader"
                    @click="generateClientToken"
                  >
                    {{ $t("companyDetails.form.generateToken") }}
                  </button>

                  <b-spinner v-if="tokenLoader" />
                </div>
              </Transition>

              <!-- Token Form -->
              <div v-if="token" class="token-form">
                <div
                  class="d-flex justify-content-between align-items-center vp-width-680"
                >
                  <vp-input
                    width="500"
                    :readOnly="true"
                    type="password"
                    :passwordIcon="token && !tokenLoader"
                    label="Token"
                    v-model="token"
                    :isLoading="tokenLoader"
                  />

                  <button
                    v-show="token"
                    id="copy-text-btn"
                    class="btn btn-primary"
                    @click="copyText"
                  >
                    <i
                      class="copy-btn fa fa-regular fa-copy fa-lg vp-font-size-25 vp-mt-5"
                    ></i>
                  </button>

                  <button
                    class="btn btn-outline-danger"
                    :disabled="tokenLoader"
                    v-b-modal="'confirm-delete'"
                  >
                    {{ $t("companyDetails.form.deleteToken") }}
                  </button>

                  <b-tooltip
                    target="copy-text-btn"
                    variant="primary"
                    placement="top"
                  >
                    {{ $t("companyDetails.form.copyToken") }}
                  </b-tooltip>
                </div>
              </div>

              <div class="vp-mt-30" v-if="documentation_url">
                <h6 class="text-muted">
                  {{ $t("companyDetails.form.apiURL") }}
                </h6>
                <a class="doc-link" :href="documentation_url" target="_blank">{{
                  documentation_url
                }}</a>
              </div>
            </div>
          </div>
        </div>
      </template>
    </VpFormLayout>
    <b-modal id="confirm-delete" hide-footer hide-header>
      <ConfirmDeleteModal @confirm-delete="deleteToken" />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PageHeader from "@/components/page-header";
import EditEntity from "@/components/entity/NewEditEntity";
import AlertFoundActiveSession from "@/components/emergency/AlertFoundActiveSession";
// import cropImageCell from "@/components/sharedCells/CropImageCell";
import cropperImage from "@/components/sharedCells/CropperImage";
import entityService from "@/services/entity.js";
import ConfirmDeleteModal from "./ConfirmDeleteModal.vue";

export default {
  name: "companyDetails",
  components: {
    PageHeader,
    EditEntity,
    AlertFoundActiveSession,
    // cropImageCell,
    cropperImage,
    ConfirmDeleteModal,
  },
  created(){
    this.$route.meta.title = this.$t('companyDetails.form.details')
    document.title = this.$route.meta.title;
  },
  data() {
    return {
      company: null,
      cropMessage: this.$t("common.notes.logoInstructionsUpload"),
      cropErrorMessage: this.$t("common.notes.logoInstructionsUploadError"),
      cropArea: "box",
      cropType: "Logo",
      disabledEdit: false,
      cropImageData: null,
      token: "",
      tokenLoader: false,
      temperature_unit: null,
      documentation_url: null,
    };
  },
  watch: {
    getEntityDataById(entity) {
      const { setCurrentEntity } = this;
      setCurrentEntity(entity);
      console.log("this.getEntityDataById", entity);
      // this.company = entity;
      // if (this.company.setting) {
      //   this.company.setting.forEach((row) => {
      //     if (row.name == "temperature_display_unit_for_company") {
      //       this.temperature_unit = row.pivot ? row.pivot.value : "";
      //     }
      //   });
      // }

      this.company = entity;
      this.token = entity?.public_clients_token || "";
      this.documentation_url = entity?.documentation_url || null;

      entity.setting?.forEach((row) => {
        if (row.name == "temperature_display_unit_for_company") {
          this.temperature_unit = row.pivot ? row.pivot.value : "";
        }
      });

      if (
        entity?.package == "Standalone" &&
        this.userEmail != "lt_techteam@visipoint.com"
      )
        this.disabledEdit = true;
    },
  },
  computed: {
    ...mapGetters("authfack", [
      "loadingStatus",
      "getUserInfo",
      "currentEntity",
      "userDetails",
      "getUserRole",
    ]),
    ...mapGetters("entity", ["getEntityDataById"]),
    ...mapGetters("i18N", ["getLanguage"]),
    title() {
      return this.$t("companyDetails.form.details");
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
    companyPackage() {
      let companyPackage = this.company.package;

      if(companyPackage == "Cloud")
        companyPackage = this.$t("common.subscriptions.cloud")
      else
        companyPackage = this.$t("common.subscriptions.standalone")

      if (
        this.company &&
        this.company.package === "Cloud" &&
        (this.userEmail === "lt_techteam@visipoint.com" ||
          (this.currentEntity &&
            this.getUserRole &&
            ["Admin", "Owner"].includes(this.getUserRole.name)))
      ) {
        return `${companyPackage} (${this.getPlanName(
          this.company.plan
        )})`;
      } else {
        return companyPackage;
      }
    },
  },
  methods: {
    ...mapActions("modal", ["setData"]),
    ...mapActions("authfack", ["setCurrentEntity"]),
    ...mapActions("entity", [
      "fetchEntityDataById",
      // "fetchCropImageData"
    ]),
    getPlanName(plan) {
      let planText = "";
      switch (plan) {
        case 1:
          planText = this.$t("common.plans.lite");
          break;
        case 2:
          planText =this.$t("common.plans.professional");
          break;
        case 3:
          planText =this.$t("common.plans.enterprise");
          break;
        default:
          planText = this.$t("common.plans.enterprise");
          break;
      }
      return planText;
    },
    copyText() {
      navigator.clipboard.writeText(this.token);
      this.$toast.success("Token Copied");
    },
    async generateClientToken() {
      try {
        this.tokenLoader = true;
        const {
          data: { token: token, document_url: documentation_url },
        } = await entityService.createClientToken();

        this.token = token;
        this.documentation_url = documentation_url;
      } catch (err) {
        console.log(err);
      } finally {
        this.tokenLoader = false;
      }
    },
    async deleteToken() {
      try {
        this.tokenLoader = true;
        await entityService.deleteClientToken();
        this.token = "";
        this.documentation_url = null;
      } catch (err) {
        console.log(err);
      } finally {
        this.tokenLoader = false;
      }
    },
    showEditEntityModal(data) {
      this.$bvModal.show("editEntity");
      if (data) {
        this.cropImageData = null;
        setTimeout(() => {
          this.cropImageData = data;
        }, 50);
      }
    },
    updateCompanyData(newCompanyData) {
      const { data: companyData } = newCompanyData;

      if (!newCompanyData) {
        console.error("Could not set new company data");
        return;
      }
      this.setCompanyData(companyData);
    },
    setCompanyData(companyData) {
      this.company = companyData;
      this.token = companyData?.public_clients_token || "";
      this.documentation_url = companyData?.documentation_url || null;

      companyData.setting?.forEach((row) => {
        if (row.name == "temperature_display_unit_for_company") {
          this.temperature_unit = row.pivot ? row.pivot.value : "";
        }
      });

      if (
        companyData?.package == "Standalone" &&
        this.userEmail != "lt_techteam@visipoint.com"
      )
        this.disabledEdit = true;
    },
  },
  async beforeMount() {
    await this.fetchEntityDataById();
  },
};
</script>

<style lang="scss" scoped>
.doc-link:hover {
  cursor: pointer;
}
p.paragraph-muted {
  margin-bottom: 23px !important;

  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 19px !important;
  color: #495057 !important;
}
h6.text-muted {
  font-weight: 700 !important;
  font-size: 14px !important;
  line-height: 17px !important;
  color: #c7c7c7 !important;
}
.seperator-line {
  height: 1px;
  // width: 990px !important;
  margin: 0px auto 33px auto;
  background: #000000;
  opacity: 0.08;
}
button.btn.edit-btn.btn-primary.btn-sm.rounded-pill {
  width: 111px !important;
  height: 33px !important;
  border-radius: 16.6275px !important;

  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 19px !important;
  text-align: center;
  margin-bottom: 21px !important;
  color: #ffffff !important;
}
p.text-muted {
  font-weight: 700 !important;
  font-size: 12px !important;
  line-height: 17px !important;
  margin-bottom: 20px !important;

  text-align: center;
  letter-spacing: 0.25px !important;

  color: #939393 !important;

  opacity: 0.8 !important;
}
.card-body {
  height: fit-content;
  padding: 33px 45px 38px 45px !important;
}
.camera-BG {
  display: inline-flex;
  width: 106px;
  height: 106px;
  align-items: center;
  justify-content: center;
  background: #f2f2f2;
  border-radius: 50%;
  text-align: center;
  margin-bottom: 17px;
}

.img-company-bg {
  width: 250px;
  height: 83px;
  border: none;
  border-radius: 18px;
  position: relative;
  margin-bottom: 14px;
}

.mdi {
  font-size: 25pt;
  color: #8c8c8c;
  opacity: 0.65;
}

.company-info__card {
  position: relative;

  & > div {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;

      @media screen and (max-width: 991px) {
        margin-bottom: 20px;
      }
    }
  }

  h6 {
    margin-bottom: 7px;
  }
  p {
    margin-bottom: 0;
  }

  &:after {
    content: "";
    position: absolute;
    width: 1px;
    height: 80%;
    inset-inline-end: 0;
    top: 10%;
    opacity: 0.08 !important;
    background-color: #000000 !important;

    @media screen and (max-width: 991px) {
      display: none;
    }
  }

  &:last-child::after {
    display: none;
  }

  @media screen and (min-width: 992px) {
    padding: 0 60px;

    &:first-child {
      padding-inline-start: 12px;
    }

    &:last-child {
      padding-inline-end: 12px;
    }
  }
}

.copy-btn {
  cursor: pointer !important;
}
</style>

<style lang="scss">
.newCompany .fixed-footer {
  display: none;
}
.tooltip .tooltip-inner {
  border-radius: 6px;

  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 14px !important;
  text-align: center;
}
.md-input-action,
.md-toggle-password {
  right: unset !important;
  inset-inline-end: 0 !important;
}
</style>
